import { call, fork, put, select, take } from 'redux-saga/effects'
import { delay } from 'redux-saga'
import { pathToAction } from 'redux-first-router'

import api from 'api'
import { NAME as ROUTE_FIND } from 'views/FindActivity/constants'
import { NAME as ROUTE_PROFILE } from 'views/Profile/constants'
import { NAME as ROUTE_LOGIN } from 'views/Login/constants'
import { reverse, locationSelector } from 'utilities/routes'
import { resetState } from 'state/actions'

import actions from './actions'

export function* loginFlow() {
  const loginRoutes = [ROUTE_LOGIN]

  while (true) {
    const request = yield take(actions.login.toString())
    const location = yield select(locationSelector)

    const { next } = location.query
    const nextRoute = next ? pathToAction(next, location.routesMap) : null

    // This variable will be filled up with data through a series of requests.
    let account = null

    // Get user.
    try {
      if (request.payload) {
        const { username, password } = request.payload
        yield call([api.client, 'authenticate'], username, password)
      }
      const userResponse = yield call([api.user, 'getCurrent'])
      account = {
        ...account,
        id: userResponse.data.id,
        name: userResponse.data.email,
        signupFlow: userResponse.data.signup_flow,
      }
    } catch (_error) {
      yield put(actions.set({ account }))
      yield put(
        actions.loginError(
          request.payload
            ? 'Invalid username or password'
            : 'Invalid authentication token.',
        ),
      )
      continue
    }

    // Get profile (if any).
    try {
      const profileResponse = yield call([api.profile, 'getCurrent'])
      const profile = profileResponse.data
      yield put(actions.loginSuccess())
      if (profile) {
        account = {
          ...account,
          id: profile.user_id,
          profileId: profile.id,
          name: profile.first_name,
        }
        // Redirect only if on login page
        if (loginRoutes.indexOf(location.type) !== -1 || next) {
          yield put(nextRoute || reverse(ROUTE_FIND))
        }
      }
    } catch (_error) {
      if (loginRoutes.indexOf(location.type) !== -1) {
        yield put(nextRoute || reverse(ROUTE_PROFILE))
      }
    }

    // Get employee (if any).
    try {
      const { data: employee } = yield call([api.employee, 'getCurrent'])
      if (employee) {
        account = {
          ...account,
          employeeId: employee.id,
          image: {
            src: employee.picture && employee.picture.url,
          },
        }
      }
    } catch (error) {
      // This errors if the user isn’t an employee (404), in which case we
      // want to ignore the error. It can also error if the user _is_ an
      // employee, but the request fails. That just results in no profile
      // picture, so it doesn’t matter much.
    }

    try {
      const { data: parent } = yield call([api.parent, 'getCurrent'])
      if (parent) {
        account = {
          ...account,
          parentId: parent.id,
        }
      }
    } catch (error) {
      // This errors if the user isn’t a parent (404), in which case we
      // want to ignore the error.
    }

    // Finally put the account into Redux. Doing this once at the end avoids
    // flashing the email address (which is used as name for users without
    // profiles) until the profile loads.
    yield put(actions.set({ account }))
  }
}

export function* logoutFlow() {
  while (true) {
    yield take(actions.logout.toString())
    yield call([api.client, 'logout'])
    yield put(resetState())
    yield put(actions.set({ account: null, accountKey: Date.now() }))
  }
}

export default function*() {
  yield fork(loginFlow)
  yield fork(logoutFlow)
  if (api.client.isAuthenticated()) {
    yield put(actions.login())
  } else if (typeof window !== 'undefined') {
    // Mark as not logged in when running in the browser.
    yield delay(0)
    yield put(actions.set({ account: null }))
  }
}



// WEBPACK FOOTER //
// ../source/views/App/sagas.js